import { useEffect, useState } from "react";

import { useMedplum } from "@medplum/react";

import { Accordion, Flex, Paper, Stack, Title } from "@mantine/core";

import { IconNotes } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";

import classes from "../PhysicalExams.module.css";
import { getPhysicalExamDiffHistory, PhysicalExamDiffResult } from "../services/physical-exam-diff";
import { PhysicalExamsDiffProps } from "../types/physical-exams";

import PhysicalExamsDiffGrid from "./PhysicalExamsDiffGrid";

const PhysicalExamsDiff = ({
  currentVersionIndex,
  versionId,
  encounter,
}: PhysicalExamsDiffProps) => {
  const medplum = useMedplum();
  const [histories, setHistories] = useState<PhysicalExamDiffResult>({});

  useEffect(() => {
    const getHistory = async () => {
      const histories = await getPhysicalExamDiffHistory(
        medplum,
        encounter,
        versionId,
        currentVersionIndex,
      );
      setHistories(histories);
    };

    getHistory();
  }, [medplum, encounter, currentVersionIndex, versionId]);

  // Check if there's any physical exam data in the histories
  const hasAnyData =
    Object.keys(histories).length > 0 &&
    Object.values(histories).some(
      (historyData) =>
        historyData &&
        historyData.length > 0 &&
        historyData.some(([current, previous]) => current || previous),
    );

  if (!hasAnyData) {
    return (
      <Paper p="lg" withBorder>
        <Title mb="lg" order={2}>
          Physical Exams
        </Title>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No physical exam data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack>
        <Title mb="lg" order={3}>
          Physical Exams
        </Title>
        {Object.keys(histories).map((title) => {
          return (
            <Accordion
              multiple
              chevronPosition="right"
              key={`${title}-accordion`}
              data-testid="physical-exams-accordion"
              classNames={{ control: classes.control }}
              styles={{
                label: { padding: 0 },
                content: { padding: 0 },
              }}
            >
              <Accordion.Item
                value={title}
                key={`${title}-item`}
                style={{ border: "1px solid var(--mantine-color-cream-4)", borderRadius: 4 }}
              >
                <Accordion.Control p="lg">
                  <Flex justify="space-between">
                    <Flex align="center">
                      <IconNotes size={24} color="#a0a0a0" style={{ marginRight: 8 }} />
                      <TextWrapper
                        c="charcoal.0"
                        variant="bold"
                        aria-label={`Physical Exam ${title}`}
                      >
                        {title}
                      </TextWrapper>
                    </Flex>
                  </Flex>
                </Accordion.Control>
                <Accordion.Panel>
                  {histories !== undefined && (
                    <PhysicalExamsDiffGrid title={title} histories={histories} />
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default PhysicalExamsDiff;
